import {ISceneAddon, SceneAddonType, WallAddonPosition} from "./ISceneAddon";
import {SceneNodeState} from "../ISceneNode";
import {LeanToConfig} from "../../config/LeanToConfig";
import {Main} from "../../Main";
import {AddonManager} from "./AddonManager";
import {Mesh, Material, Raycaster, MeshBasicMaterial, BoxGeometry, Scene as Scene3D, Object3D, Box3, MeshStandardMaterial} from "three";
import {MaterialManager} from "../../material/MaterialManager";
import {Building, WallSide} from "../../building/Building";
import {MathUtils} from "../../utils/MathUtils";
import {NodePlacer} from "../../controller/NodePlacer";
import {PostManager} from "../../building/post/PostManager";
import {ObjectUtils} from "../../utils/data/ObjectUtils";
import {Scene} from "../Scene";
import TWEEN from "@tweenjs/tween.js";
import {ArrayUtils} from "utils/ui/ArrayUtils";

/**
 * Common class to deal with wall addons:
 * doors, windows.
 * These can be added on the walls of the building.
 */
export class WallAddon implements ISceneAddon
{
	public static readonly addonHeightOffset = 1;
	private static count = 0;

	public name: string;
	public itemId: string;
	public id: string;
	public type = SceneAddonType.WALL;
	public state: SceneNodeState;
	public added = false;
	public hasValidPlace = false;

	public width: number;
	public height: number;
	private _verticalAlign: string;
	private _isValid: boolean;
	private _buildingLength: number;
	private _buildingWidth: number;
	private _leanToConfig: LeanToConfig;
	private _building: Building;

	private _pos: {
		wall: WallSide;
		horizontal: number; // horizontal (may be x or z)
		vertical: number; // vertical
		x: number;
		y: number;
		z: number;
	};

	private _object: Object3D;
	private _material: MeshStandardMaterial[];
	private _columnOffset: number = 0.35;
	private _offset: number;

	private _boundingBox: Mesh;

	private _itemConfig: any;

	// Not used currently
	private _visible = true;

	private _tweenA: any;
	private _tweenB: any;

	public static isAddonRollup(addon: ISceneAddon, includingFrames: boolean = true)
	{
		const rollup = addon;
		if (rollup instanceof WallAddon)
		{
			if (rollup.itemId.substring(0, 6) === "rollup")
			{
				if (!includingFrames && rollup.itemId.includes("frame"))
				{

				}
				else
				{
					return true;
				}
			}
		}
		return false;
	}

	public static getTopOfAddonWithOffset(addon: ISceneAddon)
	{
		return addon.getBBox().max.y + WallAddon.addonHeightOffset;
	}

	constructor(itemConfig: any, wall: WallSide)
	{
		this._building = Main.getInstance().scene.building;
		const config = Main.getInstance().config;

		this._buildingLength = config.building.length.value;
		this._buildingWidth = config.building.width.value;
		this._isValid = true;
		this._itemConfig = itemConfig;
		this.name = itemConfig.label;
		this.itemId = itemConfig.id;
		this.id = itemConfig.id + "_" + (++WallAddon.count);
		this._leanToConfig = Main.getInstance().config.building.leanto;

		const type = this.id;
		const isItRollup = type.substring(0, 6) === "rollup";
		const isItWalkDoor = type.substring(0, 5) === "door_";
		this._offset = isItRollup ? 1.5 : this._columnOffset; // rollups have to be 2 feet away from columns (+columnwidth/2), a 16' rollup must fit between a 2 columns, if they're 20' away from each other

		this._material = this.getMaterial(itemConfig);

		this._object = AddonManager.getInstance().createMesh(itemConfig.mesh, this._material);

		if(isItRollup){
			this._object.children[0].castShadow = true;
			this._object.children[1].castShadow = true;
			this._object.children[2].castShadow = true;
		}

		if(isItWalkDoor){
			this._object.children[0].castShadow = true;
			this._object.children[1].castShadow = true;
		}

		if (itemConfig.visible === false)
		{
			this._visible = false;
			this._object.visible = false;

			//this.getBoundingBox();
		}

		if (itemConfig.rollupFrame)
		{
			this.hideRollupDoor(this._object);
		}

		// hide it initially, it will be visible if a valid mouse position is used
		// (don't show it initially in a random place)
		//this.hide();


		this.width = itemConfig.size.width;
		this.height = itemConfig.size.height;

		this._verticalAlign = itemConfig.verticalAlign;

		this._pos = {
			wall: wall,
			horizontal: 0,
			vertical: this.height / 2,
			x: 0, // derived
			y: 0, // derived
			z: 0  // derived
		};

		this.updatePosition();

		if (isItRollup && !this.id.includes("frame"))
		{
			const signalConfig = Main.getInstance().config.colors.rollUp;
			signalConfig.signal.add(this.onRollUpColorChange);
		}
	}

	private onRollUpColorChange = (value: number) =>
	{
		const rollUpMaterial = <MeshStandardMaterial>this._material[1];
		rollUpMaterial.color.set(value);
	}

	private hideRollupDoor(obj: Object3D)
	{
		// For rollup frames, we only want to show the trim, not the door itself.

		obj.children[1].visible = false;
	}

	private getMaterial(itemConfig: any): MeshStandardMaterial[]
	{
		const color = itemConfig.color;

		let material: MeshStandardMaterial[];


		if (this._itemConfig.rollupFrame)
		{
			material = MaterialManager.getInstance().addons.rollup_frame.map(value => value.clone());
		}
		else
		{
			material = MaterialManager.getInstance().addons[color].map(value => value.clone()); // rollup, wall or door
		}

		return material;
	}

	public addToScene(Scene3D: Scene3D, placed: boolean = false)
	{
		if (this._visible)
		{
			Scene3D.add(this._object);

			if (placed)
			{
				this.updateMaterial(placed);
			}
		}
		else
		{
			Scene3D.add(this.getBoundingBox());
		}
	}

	public removeFromScene(Scene3D: Scene3D)
	{
		if (this._visible)
		{
			Scene3D.remove(this._object);

			for (const mesh of this._object.children)
			{
				(<MeshStandardMaterial>(<Mesh>mesh).material).needsUpdate = true;
			}
		}
		else
		{
			Scene3D.add(this.getBoundingBox());
		}
	}

	public updatePosition(): boolean // returns the addon's position's validity
	{
		const config = Main.getInstance().config;

		const pos = this._pos;
		const wall = pos.wall;

		const widthHalf = this.width / 2;

		const buildingWidthHalf = config.building.width.value / 2;
		const buildingLengthHalf = config.building.length.value / 2;

		// edgeConstraint is that doors cannot be placed on the edge, there has to be a limit
		const edgeConstraint = Main.getInstance().config.config.assets.addon.constraints.edge;

		// TODO: for roll ups only, this would be better done on the model (blender)
		let inset_offset = 0.0;
		if (this._itemConfig.color === "rollup")
		{
			inset_offset = 0.5;
		}

		let x: number;
		let z: number;
		let y: number;

		let vertical: number;
		let horizontal: number;

		if (wall === Building.WALLS.FRONT || wall === Building.WALLS.BACK)
		{
			x = MathUtils.clamp(
				pos.horizontal, -buildingWidthHalf + widthHalf + edgeConstraint,
				buildingWidthHalf - widthHalf - edgeConstraint
			);

			horizontal = x;

			z = buildingLengthHalf - inset_offset;

			if (wall === Building.WALLS.FRONT)
			{
				this._object.rotation.y = 0;
			}
			else
			{
				z = -z;
				this._object.rotation.y = Math.PI;
			}
		}
		else // if (wall === Building.WALL_LEFT || wall === Building.WALL_RIGHT)
		{
			z = MathUtils.clamp(
				pos.horizontal, -buildingLengthHalf + widthHalf + edgeConstraint,
				buildingLengthHalf - widthHalf - edgeConstraint
			);

			horizontal = z;

			x = buildingWidthHalf - inset_offset;

			if (wall === Building.WALLS.LEFT)
			{
				x = -x;
				this._object.rotation.y = -Math.PI / 2;
			}
			else
			{
				this._object.rotation.y = Math.PI / 2;
			}
		}

		if (this._verticalAlign === "bottom")
		{
			const heightHalf = this.height / 2;
			y = heightHalf;
		}
		else
		{
			y = pos.vertical;
		}
		vertical = y;

		const isPositionValid = !this.hitTestAddons(horizontal, vertical);

		if (!isPositionValid)
		{
			if (!this._visible)
			{
				this.getBoundingBox();
			}

			// Don't allow 2 doors to intersect within 2 feet of each other
		}
		else
		{
			// We only save if hittest is false

			pos.horizontal = horizontal;
			//pos.vertical = vertical;
		}

		this._object.position.x = x;
		this._object.position.y = y;
		this._object.position.z = z;

		if (!this._visible)
		{
			this.getBoundingBox();
		}

		return isPositionValid;
	}

	/**
	 * A method for highlighting the mesh for a couple of seconds
	 */
	public highlight()
	{
		if (this._tweenA)
		{
			this._tweenA.stop();
		}
		if (this._tweenB)
		{
			this._tweenB.stop();
		}

		this._tweenA = this.getHighlightTween(1000, 0.75, 0.4);
		this._tweenB = this.getHighlightTween(2500);

		this._tweenA.repeat(1).chain(this._tweenB);
		this._tweenA.start();
	}

	private getHighlightTween(animationDuration: number, startValue: number = 0.75, endValue: number = 0)
	{
		const startData = {
			intensity: startValue
		};
		const endData = {
			intensity: endValue
		};

		return new TWEEN.Tween(startData)
			.to(endData, animationDuration)
			.easing(TWEEN.Easing.Quadratic.In) // Use an easing function to make the animation smooth.
			.onUpdate(() =>
			{
				for (const material of this._material)
				{
					material.emissive.set(0x00FFFF);
					material.emissiveIntensity = startData.intensity;
				}
				Main.getInstance().render = true;
			})
			.onComplete(() =>
			{
				for (const material of this._material)
				{
					material.emissive.set(0x00FFFF);
					material.emissiveIntensity = endData.intensity;
				}
				Main.getInstance().render = true;
			});
	}

	private hitTestAddons(horizontal: number, vertical: number): boolean
	{
		if (!this._pos.wall)
		{
			return true;
		}

		const addons = Main.getInstance().scene.building.addons[this._pos.wall];

		for (let i = 0; i < addons.length; ++i)
		{
			if (this.hitTestAddon(addons[i], horizontal, vertical))
			{
				return true;
			}
		}

		return false;
	}

	private hitTestAddon(addon: ISceneAddon, horizontal: number, vertical: number)
	{
		if (addon === <any>this)
		{
			return false;
		}

		const distanceToKeep = 2; // 2 feet

		const w = this.width;
		const h = this.height;

		const r1x1 = horizontal - w / 2 - distanceToKeep;
		const r1x2 = horizontal + w / 2 + distanceToKeep;
		const r1y1 = vertical - h / 2;
		const r1y2 = vertical + h / 2;

		if (addon instanceof WallAddon)
		{
			const addonW = addon.width;
			const addonH = addon.height;

			const r2x1 = addon._pos.horizontal - addonW / 2;
			const r2x2 = addon._pos.horizontal + addonW / 2;
			const r2y1 = addon._pos.vertical - addonH / 2;
			const r2y2 = addon._pos.vertical + addonH / 2;

			return r1x1 < r2x2 && r2x1 < r1x2 &&
				r1y1 < r2y2 && r2y1 < r1y2;
		}

		return false;
	}

	public checkPlacing(raycaster: Raycaster, Scene3D: Scene, nodePlacer: NodePlacer): boolean
	{
		this._buildingWidth = Main.getInstance().config.building.width.value;
		this._buildingLength = Main.getInstance().config.building.length.value;
		const intersects = raycaster.intersectObjects(nodePlacer.hitScene.children);

		const intersect = intersects[0];

		if (intersect)
		{
			const intersectedWall = <Mesh>intersect.object;
			const intersectedWallName = intersectedWall.name as WallSide;

			if (intersectedWallName !== Building.WALLS.FRONT &&
				intersectedWallName !== Building.WALLS.BACK &&
				intersectedWallName !== Building.WALLS.LEFT &&
				intersectedWallName !== Building.WALLS.RIGHT)
			{
				// intersection is not a wall (could be a roof)
				return false;
			}

			// At this point, intersection is a wall

			this._pos.wall = intersectedWallName;

			const point = intersect.point;

			if (intersectedWallName === Building.WALLS.FRONT || intersectedWallName === Building.WALLS.BACK)
			{
				// front or back wall

				this._pos.horizontal = this.pushAwayFromColumns(point.x, this._buildingWidth) ?? this._pos.horizontal ?? 0;
			}
			else
			{
				// right or left wall

				this._pos.horizontal = this.pushAwayFromColumns(point.z, this._buildingLength) ?? this._pos.horizontal ?? 0;
			}

			if (this._verticalAlign === undefined)
			{
				// no verticalAlign -> y position is adjusted by user

				const heightHalf = this.height / 2;
				const buildingConfig = Scene3D.config.building;

				let y = point.y;
				let singleSlopeHeight = 0;
				const building = Main.getInstance().config.building;
				const offset = 0.5; // we need this for single slope, because the support beam appeared behind the window before
				if (building.single_slope.value && intersectedWallName !== Building.WALLS.LEFT)
				{
					singleSlopeHeight = building.width.value * building.pitch.value / 12;
				}
				else
				{
					if (intersectedWallName === Building.WALLS.FRONT || intersectedWallName === Building.WALLS.BACK)
					{
						singleSlopeHeight = building.width.value * building.pitch.value / 24;
					}
				}
				y = MathUtils.clamp(y, heightHalf, buildingConfig.height.value + singleSlopeHeight - offset - heightHalf);

				if (this.id.substr(0, 6) === "window")
				{

					y = this.clampBetweenGirts(point.x, y, buildingConfig.height.value + singleSlopeHeight - offset, intersectedWallName);
				}

				this._pos.vertical = y;
			}

			if (this.hitTestAddons(this._pos.horizontal, this._pos.vertical))
			{
				this.hasValidPlace = false;
				// Don't allow 2 doors to intersect within 2 feet of each other
				return false;
			}
			this.show();
		}

		if (this._pos.wall)
		{
			const tabuPositions = this.getTabuPositions(this._pos.wall === Building.WALLS.FRONT || this._pos.wall === Building.WALLS.BACK ? this._buildingWidth : this._buildingLength);
			const neighbourTabuPositions = this.getNeighbourTabuPositions(this._pos.horizontal, tabuPositions);
			if (neighbourTabuPositions)
			{
				this.hasValidPlace = this.updatePosition() && this.checkPositionForColumns(this._pos.horizontal, this._offset, neighbourTabuPositions[0], neighbourTabuPositions[1]);
			}
			else
			{
				this.hasValidPlace = false;
			}
		}
		else
		{
			this.hasValidPlace = false;
		}
		

		//if (!this.hasValidPlace)
		//{
		//	this.hide();
		//}

		return !!intersect;
	}

	private updateMaterial(placed: boolean)
	{

		// TODO revise why this is needed

		this._material[0].needsUpdate = true;
		this._material[1].needsUpdate = true;

		if (this._material[2] != undefined) // ROLLUPS have an inner layer, which is always white
		{
			this._material[2].needsUpdate = true;
		}

		/*
		const material = this._material;

		if (placed)
		{
			// node is placed on the real Scene3D
			material.ambient.set(this._ambient);
			material.transparent = false;
		}
		else
		{
			// node is still on the temporary overlay Scene3D

			material.transparent = true;
			material.opacity = 0.8;

			if (this.hasValidPlace)
			{
				this._material.ambient.set(0x00ff00);
			}
			else
			{
				this._material.ambient.set(0xffff00);
			}

			if (this.height > this.s)
		}
		*/
	}

	public hide()
	{
		if (this._visible)
		{
			this._object.visible = false;
		}
		else
		{

			if (this._pos)
			{
				this.getBoundingBox().visible = false;
			}
		}
	}

	public show()
	{
		if (this._visible)
		{
			this._object.visible = true;
		}
		else
		{
			this.getBoundingBox().visible = true;
		}
	}

	public getBoundingBox(): Mesh
	{
		if (!this._boundingBox)
		{
			// first time bounding box is queried -> create it

			// this has an effect on Building::cleanWallGeometry
			const boxDepth = 1.5;
			const geom = new BoxGeometry(this.width, this.height, boxDepth, 1, 1, 1);

			let material: Material = null;
			if (!this._visible)
			{
				material = new MeshBasicMaterial({color: 0xcccccc});
			}

			this._boundingBox = new Mesh(geom, material);
			// This is needed in case _visible is false (for Frames)
			this._boundingBox.visible = false;
		}

		this.addTransformToBoundingBox(this._boundingBox);

		return this._boundingBox;
	}

	public getBBox(): Box3
	{
		return new Box3().setFromObject(this._object);
	}

	private addTransformToBoundingBox(boundingBox: Mesh)
	{
		if (!this._pos)
		{
			return;
		}

		boundingBox.position.set(this._object.position.x, this._object.position.y, this._object.position.z);

		// an addon can change walls (when moved to a different wall)
		const isSideWall = this.parentId === Building.WALLS.LEFT || this.parentId === Building.WALLS.RIGHT;
		if (isSideWall)
		{
			boundingBox.rotation.y = Math.PI / 2;
		}
		else
		{
			boundingBox.rotation.y = 0;
		}
	}

	private clampBetweenGirts(x: number, y: number, buildingHeight: number, wallId: string)
	{
		const spaceBetweenGirts = 3.667;
		const girtHeight = 0.41525 / 2;
		//y = ((spaceBetweenGirts) / 2) + spaceBetweenGirts;

		let minDeltaY = 99999;
		let minDeltaK = 0;

		const building = Main.getInstance().config.building;
		if (wallId === Building.WALLS.FRONT || wallId === Building.WALLS.BACK)
		{
			if (building.single_slope.value)
			{
				const buildingHeightOnAddonEdge_1 = building.height.value + (building.width.value / 2 + x - this.width / 2) * building.pitch.value / 12;
				const buildingHeightOnAddonEdge_2 = building.height.value + (building.width.value / 2 + x + this.width / 2) * building.pitch.value / 12;
				buildingHeight = Math.max(Math.min(buildingHeightOnAddonEdge_1, buildingHeightOnAddonEdge_2), building.height.value);
			}
			else
			{
				const buildingTop = buildingHeight;
				const buildingHeightOnAddonEdge_1 = buildingTop - Math.abs((x - this.width / 2) * building.pitch.value / 12);
				const buildingHeightOnAddonEdge_2 = buildingTop - Math.abs((x + this.width / 2) * building.pitch.value / 12);
				buildingHeight = Math.max(Math.min(buildingHeightOnAddonEdge_1, buildingHeightOnAddonEdge_2), building.height.value);
			}
			const offset = 1;
			buildingHeight -= offset;
		}

		for (let k = 0; (k + 1) * spaceBetweenGirts < buildingHeight; ++k)
		{
			if (Math.abs(spaceBetweenGirts / 2 + k * spaceBetweenGirts - y) < minDeltaY)
			{
				minDeltaY = Math.abs(spaceBetweenGirts / 2 + k * spaceBetweenGirts - y);
				minDeltaK = k;
			}
		}

		y = ((spaceBetweenGirts) / 2) + minDeltaK * spaceBetweenGirts;

		if (minDeltaK === 0)
		{
			y -= girtHeight;
		}

		return y;
	}

	private getNeighbourTabuPositions(z: number, tabuPositions: number[])
	{
		for (let i = 0; i < tabuPositions.length - 1; ++i)
		{
			if (tabuPositions[i] < z && z < tabuPositions[i + 1])
			{
				return [tabuPositions[i], tabuPositions[i + 1]];
			}
		}

		return null;
	}

	private clampPositionBetweenColumns(z: number, offset: number, leftColumn: number, rightColumn: number)
	{
		const addonWidth = this.width;

		const minValue = leftColumn + addonWidth / 2 + offset;
		const maxValue = rightColumn - addonWidth / 2 - offset;
		if (minValue > maxValue)
		{
			return null;
		}

		return MathUtils.clamp(z, minValue, maxValue);
	}

	private checkPositionForColumns(z: number, offset: number, leftColumn: number, rightColumn: number)
	{
		return leftColumn < z - this.width / 2 - offset && z + this.width / 2 + offset < rightColumn;
	}

	private getNumberOfBays(wallLength: number)
	{
		const isItTheFront = this._pos.wall === "front";
		const isItTheBack = this._pos.wall === "back";

		return isItTheFront || isItTheBack ? Math.ceil(wallLength / PostManager.IntervalOnEnd) : Math.ceil(wallLength / PostManager.IntervalOnSide);
	}

	private getTabuPositions(wallLength: number)
	{
		const isItTheFront = this._pos.wall === "front";
		const isItTheBack = this._pos.wall === "back";
		const isRollup = this.id.includes("rollup");

		const addons = this._building.addons[this._pos.wall];
		const rollups = addons.filter((addon: WallAddon) => addon.id.includes("rollup"));
		const tabuPositions = [];
		tabuPositions.push(-wallLength / 2 + this._columnOffset);
		const supportBeams = this._building.supportBeam.beams;
		const visibleSingleCees = supportBeams.singleCees[this._pos.wall].filter(s => s.visible);

		for (const singleCee of visibleSingleCees)
		{
			if (isItTheFront || isItTheBack)
			{
				if (!isRollup)
				{
					tabuPositions.push(singleCee.position.x);
				}
			}
			else
			{
				tabuPositions.push(singleCee.position.z);
			}
		}

		const leanTo = this._building.leanToManager.getLeanTo(this._pos.wall);
		const leantoPosts = leanTo.config.enabled.value ? leanTo.postManager.postTransforms : [];

		for (const leantoPost of leantoPosts)
		{
			const multiplicator = this._pos.wall === "right" ? -1 : 1;
			tabuPositions.push(multiplicator * (leantoPost.position + leantoPost.offset));
		}

		for (const rollup of rollups)
		{
			if (rollup !== this)
			{
				const rollupPosition = rollup.getPosition() as WallAddonPosition;
				tabuPositions.push(rollupPosition.horizontal - rollup.width / 2);
				tabuPositions.push(rollupPosition.horizontal + rollup.width / 2);
			}
		}
		tabuPositions.push(wallLength / 2 - this._columnOffset);

		return ArrayUtils.removeDuplicates(tabuPositions.sort((a: number, b: number) => a - b));
	}

	public pushAwayFromColumns(z: number, wallLength: number)
	{
		const isItTheFront = this._pos.wall === "front";
		const isItTheBack = this._pos.wall === "back";

        /*if (((isItTheFront && !this._leanToConfig.front.enabled.value) || (isItTheBack && !this._leanToConfig.back.enabled.value)) && isItRollup)
        {
			return 0; // (rollups are always centered in the front and the back when there's no leanto)
        }*/

		const numberOfBays = this.getNumberOfBays(wallLength);
		const sizeOfABay = wallLength / numberOfBays;
		const condition1 = sizeOfABay < this.width + 2 * this._offset && !(isItTheFront || isItTheBack);
		const condition2 = wallLength < this.width + 2 * this._offset;
		if (condition1 || condition2)
		{
			this.hasValidPlace = false;
			return;
		}
		this.hasValidPlace = true;
		
		const tabuPositions = this.getTabuPositions(wallLength);
		const neighbourTabuPositions = this.getNeighbourTabuPositions(z, tabuPositions);
		if (neighbourTabuPositions)
		{
			z = this.clampPositionBetweenColumns(z, this._offset, neighbourTabuPositions[0], neighbourTabuPositions[1]);
		}
		else
		{
			z = null;
		}

		if (z == null)
		{
			this.hasValidPlace = false;
			return;
		}

		return z;
	}

	public setPosition(value: any): boolean
	{
		this._pos = value;

		return this.updatePosition();
	}

	public getPosition()
	{
		return ObjectUtils.clone(this._pos);
	}

	public get parentId()
	{
		return this._pos.wall;
	}
	public isValid()
	{
		return this._isValid;
	}
}
